// The printed route sheet handed over with a bundle of leaflets, for people
// who'd rather not use the app. Two A4 pages: the zone on a street map with
// the job's details, then the street checklist, what to do when finished
// (with a QR code to the app) and the delivery guidelines.
//
// Built as a standalone page like the trade documents, so the preview and
// the PDF (lib/pdf.js) are the same thing.
import QRCode from 'qrcode';
import { esc } from './tradeDocs';
import { formatDay, todayYmd } from './trade';
import { renderZoneMap } from './staticMap';
import { APP_URL, formatCount, parseGuidelines, OUTLINE_COLOUR, ROUTE_COLOUR } from './leaflets';

// The map fills the content width of the first page (A4 less 13mm a side)
const MAP_W = 695;
const MAP_H = 690;
// Instructions go under the map when they fit in about 6 lines (measured: 5
// lines leave ~50px spare on the page); longer ones go overleaf.
const CHARS_PER_LINE = 100;
const LINES_UNDER_MAP = 6;
const fitsUnderMap = (text) => text.split('\n')
  .reduce((n, line) => n + Math.max(1, Math.ceil(line.length / CHARS_PER_LINE)), 0) <= LINES_UNDER_MAP;

const CSS = `
@page{size:A4;margin:12mm 13mm 11mm}
body{font-size:11.5px;line-height:1.5;color:#2a3b32}
.page{display:flex;flex-direction:column;background:#fff}
.p1{height:274mm;overflow:hidden;break-after:page}
@media screen{
  body{background:#e3e8e3;padding:12px 0}
  .page{width:210mm;padding:12mm 13mm 11mm;margin:0 auto 12px;box-shadow:0 1px 4px rgba(12,32,18,.15)}
  .p1{height:297mm}
  .p2{min-height:297mm}
}
.head{display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:9px;border-bottom:1px solid #2f7a4a}
.head .wordmark{font-size:30px}
.kick{text-align:right;line-height:1.25}
.kick b{display:block;font-size:12.5px;letter-spacing:.06em;text-transform:uppercase;color:#111a14}
.kick span{font-size:10.5px;color:#5e7265}
.title{display:flex;align-items:baseline;gap:12px;margin-top:12px}
h1{font-family:'DM Serif Display',Georgia,serif;font-weight:400;font-size:30px;line-height:1.1;color:#113449}
.area{font-size:13px;color:#5e7265}
.cap{display:block;font-size:8.5px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#5e7265;margin-bottom:1px}
.facts{display:flex;flex-wrap:wrap;gap:6px 26px;margin:9px 0 11px}
.facts b{font-size:14px;color:#111a14}
.map{position:relative;border:1px solid #c3d1c3;border-radius:6px;overflow:hidden;line-height:0}
.map img{width:100%;display:block}
.nomap{display:flex;align-items:center;justify-content:center;height:${MAP_H}px;line-height:1.4;background:#eef2ee;color:#5e7265;font-size:12px}
.legend{display:flex;flex-wrap:wrap;gap:4px 18px;margin-top:7px;font-size:10.5px;color:#2a3b32}
.legend span{display:inline-flex;align-items:center;gap:6px}
.sw{display:inline-block;width:22px;height:0;border-top:3px solid ${OUTLINE_COLOUR}}
.sw.route{border-top:3px dashed ${ROUTE_COLOUR}}
.box-note{margin-top:11px;padding:9px 12px;border:1px solid #d4e0d4;border-radius:6px;background:#f5f8f5;white-space:pre-wrap}
.box-note.first{margin:0 0 14px}
h2{font-size:15px;color:#111a14;margin-bottom:2px}
h2 small{font-weight:500;color:#5e7265;font-size:12px}
.sub{font-size:10.5px;color:#5e7265;margin-bottom:9px}
.streets{list-style:none;columns:3;column-gap:18px;font-size:11px}
.streets li{display:flex;align-items:center;gap:7px;padding:2.5px 0;break-inside:avoid}
.streets .tick{flex:none;width:12px;height:12px;border:1.3px solid #2a3b32;border-radius:2px;font-size:10px;line-height:10px;text-align:center;color:#1f5a35;font-weight:700}
.streets .done{color:#8a9b90}
.streets .done s{color:#8a9b90}
.streets em{font-style:normal;font-size:9px;color:#8a9b90;white-space:nowrap}
.none{font-size:11px;color:#5e7265;font-style:italic}
.finish{display:flex;gap:18px;align-items:flex-start;margin-top:16px;padding:12px 14px;border:1.5px solid #2f7a4a;border-radius:6px;break-inside:avoid}
.finish ol{margin:5px 0 0 16px;display:grid;gap:3px}
.finish .grow{flex:1}
.qr{flex:none;width:112px;text-align:center;line-height:1.2}
.qr img{width:112px;height:112px;display:block}
.qr figcaption{margin-top:3px;font-size:8.5px;color:#5e7265;word-break:break-all}
.guide{margin-top:16px;break-inside:avoid}
.guide-cols{columns:2;column-gap:22px;font-size:10px;line-height:1.45;margin-top:5px}
.guide h3{font-size:10.5px;margin:0 0 2px;color:#111a14;break-after:avoid}
.guide ul{margin:0 0 8px 13px;break-inside:avoid}
.guide li{margin-bottom:1px}
.foot{margin-top:14px;padding-top:7px;border-top:1px solid #d4e0d4;font-size:10.5px;color:#5e7265}
`;

const lines = (s) => esc(String(s || '').trim());

export function routeSheetFileName({ zone, person, today = todayYmd() }) {
  const name = ['Leaflet round', zone.name, person?.name, today].filter(Boolean).join(' ');
  return `${name.replace(/[^\w .()-]/g, '').replace(/\s+/g, ' ').trim()}.pdf`;
}

// { zone, job, person, settings } plus the images already made: mapImage (a
// data URL, or null if the map couldn't be drawn) and qrImage.
export function routeSheetDoc({ zone, job, person, settings, mapImage, qrImage, today = todayYmd() }) {
  const instructions = [zone.instructions, job?.instructions].filter(s => s && s.trim()).join('\n\n');
  const shortNote = fitsUnderMap(instructions);
  const done = new Map((zone.streetsDone || []).map(s => [s.name, s]));
  const contact = [settings?.contactName, settings?.contactPhone].filter(Boolean).join(' on ');

  const facts = [
    person && ['For', esc(person.name)],
    job && ['Leaflets', formatCount(job.leaflets)],
    job?.givenOn && ['Given', formatDay(job.givenOn)],
    job?.dueBy && ['Finish by', formatDay(job.dueBy, { weekday: true })],
    zone.homes ? ['Homes', `about ${formatCount(zone.homes)}`] : null,
    job?.leaflet && ['Leaflet', esc(job.leaflet)],
  ].filter(Boolean);

  const note = (cls = '') => (instructions
    ? `<div class="box-note ${cls}"><span class="cap">Instructions</span>${lines(instructions)}</div>`
    : '');

  const page1 = `<section class="page p1">
  <header class="head">
    <div class="wordmark">Tear<span>Drop</span></div>
    <div class="kick"><b>Leaflet round</b><span>Printed ${formatDay(today)}</span></div>
  </header>
  <div class="title"><h1>${esc(zone.name)}</h1>${zone.area ? `<div class="area">${esc(zone.area)}</div>` : ''}</div>
  <div class="facts">${facts.map(([k, v]) => `<div><span class="cap">${k}</span><b>${v}</b></div>`).join('')}</div>
  <figure class="map">${mapImage
    ? `<img src="${mapImage}" alt="">`
    : '<div class="nomap">The map couldn\'t be drawn (no internet?). Use the street list overleaf.</div>'}</figure>
  <div class="legend">
    <span><i class="sw"></i>Deliver to every home inside the green line</span>
    ${zone.route?.length > 1 ? '<span><i class="sw route"></i>Suggested route: start at S, finish at F</span>' : ''}
  </div>
  ${shortNote ? note() : ''}
</section>`;

  const streets = zone.streets?.length
    ? `<ul class="streets">${zone.streets.map((name) => {
      const d = done.get(name);
      return d
        ? `<li class="done"><span class="tick">&#10003;</span><s>${esc(name)}</s> <em>done ${formatDay(d.at).replace(/ \d{4}$/, '')}</em></li>`
        : `<li><span class="tick"></span>${esc(name)}</li>`;
    }).join('')}</ul>`
    : '<p class="none">No street list for this zone. Follow the green line on the map.</p>';

  const login = person
    ? `scan the code to open the leaflets app, log in as <b>${esc(person.username)}</b> and tap "Log a round"`
    : 'scan the code to open the leaflets app and tap "Log a round"';
  const guide = parseGuidelines(settings?.guidelines);

  const page2 = `<section class="page p2">
  ${shortNote ? '' : note('first')}
  <h2>Streets in this zone ${zone.streets?.length ? `<small>(${zone.streets.length})</small>` : ''}</h2>
  <p class="sub">Tick each street off once it's done.${done.size ? ' Streets already done are crossed out.' : ''}</p>
  ${streets}
  <div class="finish">
    <div class="grow">
      <h2>When you've finished</h2>
      <ol>
        <li>Stop and save the walk in Strava (or whichever app you used).</li>
        <li>Take a screenshot that shows the map of your walk, the distance and the time.</li>
        <li>Send it in: ${login}.${contact ? ` Or send it to ${esc(contact)}, with how many leaflets you put through doors.` : ''}</li>
      </ol>
    </div>
    <figure class="qr"><img src="${qrImage}" alt=""><figcaption>${esc(APP_URL.replace('https://', ''))}</figcaption></figure>
  </div>
  ${guide.length ? `<section class="guide"><h2>Good delivery</h2><div class="guide-cols">${guide.map(b => `
    ${b.heading ? `<h3>${esc(b.heading)}</h3>` : ''}<ul>${b.bullets.map(t => `<li>${esc(t)}</li>`).join('')}</ul>`).join('')}</div></section>` : ''}
  ${contact ? `<div class="foot">Questions? ${esc(contact)}. Thank you!</div>` : '<div class="foot">Thank you!</div>'}
</section>`;

  return {
    title: `Leaflet round, ${zone.name}${person ? `, ${person.name}` : ''}`,
    css: CSS,
    body: page1 + page2,
    pageSize: 'A4',
    landscape: false,
    widthMm: 210,
    heightMm: 297,
  };
}

// Draws the map and the QR code, then builds the sheet.
export async function buildRouteSheet({ zone, job, person, settings }) {
  let mapImage = null;
  try {
    mapImage = await renderZoneMap({ polygon: zone.polygon, route: zone.route || [], width: MAP_W, height: MAP_H });
  } catch (e) {
    console.error('Route sheet map failed:', e);
  }
  const qrImage = await QRCode.toDataURL(APP_URL, {
    errorCorrectionLevel: 'M', margin: 1, scale: 8, color: { dark: '#113449', light: '#ffffff' },
  });
  return routeSheetDoc({ zone, job, person, settings, mapImage, qrImage });
}
